// styles.js
import styled, { css, keyframes } from 'styled-components';

/* Layout */
export const Page = styled.div`
  min-height: 100vh;
  background: #f9fafb; /* gray-50 */
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 0.5rem;
  background: #e5e7eb; /* gray-200 */
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: 100%;
  background: #a3e635; /* lime-400 */
`;

export const Container = styled.div`
  max-width: 64rem; /* ~max-w-4xl */
  margin: 0 auto;
  padding: 1rem 0;

  @media (min-width: 640px) {
    padding: 1.5rem; /* sm:p-6 */
  }
`;

/* Cards */
export const Card = styled.div`
  background: #ffffff;
  margin-bottom: 1.5rem;

  &.soft {
    background: #f3f4f6; /* gray-100 */
    border-style: dashed;
    border-width: 2px;
    border-color: #d1d5db; /* gray-300 */
  }

  &.blue {
    background: #eff6ff; /* blue-50 */
    border: 2px solid #bfdbfe; /* blue-200 */
  }

  &.green-dark {
    background: #005d1f; /* green-800 */
    color: #fff;
    border: 0;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 32px;
  }
`;

export const CardHeader = styled.div`
  padding: 0;
  margin-bottom: 24px;
`;

export const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #231f20;
  font-weight: 600;
  font-size: 20px;
`;

export const CardBody = styled.div`
  padding: 0;
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 80px;
  font-weight: 600;
  border: 0;
  cursor: pointer;
  padding: 13px 64px;
  width: 100%;
  max-width: 264px;
  min-width: 200px;
  height: 44px;
  font-size: 16px;
  font-weight: 500;

  ${({ disabled }) =>
    disabled
      ? css`
          background: #f1f5f7;
          color: #9fa4aa;
          cursor: not-allowed;
        `
      : css`
          background: #008427;
          color: #fff;
          transition: box-shadow 0.2s ease;
          &:hover {
            background: #14aa40;
            box-shadow: none;
          }
        `};
`;

export const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid #d1d5db; /* gray-300 */
  color: #374151; /* gray-700 */
  cursor: pointer;

  &.orange {
    border-color: #fdba74; /* orange-300 */
    color: #b45309; /* orange-700 */
    &:hover {
      background: #fff7ed;
    } /* orange-50 */
  }

  &.red {
    border-color: #fca5a5; /* red-300 */
    color: #b91c1c; /* red-700 */
    &:hover {
      background: #fef2f2;
    } /* red-50 */
  }

  &.green {
    border-color: #86efac; /* green-300 */
    color: #166534; /* green-700 */
    &:hover {
      background: #f0fdf4;
    } /* green-50 */
  }

  &.yellow {
    border-color: #fde68a; /* yellow-300 */
    color: #a16207; /* yellow-700 */
    &:hover {
      background: #fffbeb;
    } /* yellow-50 */
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const TwoColGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

/* Headings */
export const CenterHeader = styled.div`
  text-align: center;
  margin: 32px 0;
  @media (min-width: 640px) {
    margin-bottom: 2rem;
  }
  h1 {
    margin: 0;
    color: #005d1f;
    font-weight: 600;
    font-size: 24px;
  }
  p {
    margin: 0;
    color: #4b5563; /* gray-600 */
    font-size: 1rem;
    @media (min-width: 640px) {
      font-size: 1.125rem;
    }
  }
`;

/* Balance card content */
export const BalanceHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0;
    font-weight: 500;
    font-size: 16px;
  }
`;

export const BalanceSub = styled.p`
  margin: 0 0 0.5rem 0;
  color: #d1fae5; /* green-100 */
  font-size: 1rem;
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

export const BalanceValue = styled.p`
  margin: 0;
  font-weight: 700;
  font-size: 32px;
`;

export const BalanceFee = styled.p`
  margin: 0.5rem 0 0 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
`;

/* Form */
export const Field = styled.div`
  margin: 0;
`;

export const Label = styled.label`
  display: inline-block;
  color: #231f20;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 8px;
`;

export const TextInput = styled.input`
  width: 100%;
  margin-top: 0;
  border-radius: 8px;
  border: 1px solid ${({ $error }) => ($error ? '#D60000' : '#231F20')};
  padding: 12px 16px;
  outline: none;
  color: #231f20;

  &:focus {
    border-color: #008427;
    box-shadow: none;
  }

  &::placeholder {
    color: #6e6e6e;
  }
`;

export const Help = styled.p`
  margin: 0.25rem 0 0 0;
  color: #6e6e6e;
  font-size: 14px;
  font-weight: 500;
`;

export const FormHelp = styled.p`
  margin: 16px 0 0 0;
  color: #231f20;
  font-size: 14px;
  font-weight: 400;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.5rem;
  color: #ef4444; /* red-500 */
  font-size: 14px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

/* Spinner */
const spin = keyframes`to { transform: rotate(360deg) }`;

export const Spinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid #005d1f;
  border-top-color: transparent;
  border-radius: 9999px;
  animation: ${spin} 0.9s linear infinite;
`;

/* Exception alerts */
export const AlertCard = styled(Card)`
  ${({ $variant }) =>
    $variant === 'orange' &&
    css`
      background: #fff7ed; /* orange-50 */
      border: 2px solid #fdba74; /* orange-300 */
      color: #9a3412; /* orange-800 */
    `}
  ${({ $variant }) =>
    $variant === 'red' &&
    css`
      background: #fef2f2; /* red-50 */
      border: 2px solid #fca5a5; /* red-300 */
      color: #991b1b; /* red-800 */
    `}
`;

export const AlertRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const DismissBtn = styled(OutlineButton)`
  margin-top: 0.75rem;
  background: transparent;
`;

/* Demo section */
export const SectionCenter = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  h3 {
    margin: 0 0 0.5rem 0;
    color: #374151;
    font-weight: 600;
  }
  p {
    margin: 0;
    color: #4b5563;
    font-size: 0.875rem;
  }
`;

export const DemoBox = styled(Card)`
  &.soft {
    background: #f3f4f6;
    border-style: dashed;
    border-color: #d1d5db;
    border-width: 2px;
  }
`;
